import { yupResolver } from '~/helpers/yupResolver';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { BrandSquare } from '~/brand/BrandContext';
import { Input } from '~/components/form/Input';
import { post } from '~/helpers/fetch';
import {
  getValidatedRedirectFromStorage,
  validateRedirect,
} from '~/helpers/redirect';
import { LogoSection } from '../LogoSection';

type LoginFormData = {
  username: string;
  password: string;
  rememberMe: boolean;
};

type LoginResult = {
  redirectTo?: string;
};

type Props = {
  back: () => void;
  email?: string;
};

const VALIDATION_SCHEMA = Yup.object().shape({
  username: Yup.string().required('Required'),
  password: Yup.string().required('Required'),
});

export const LoginUsingPassword = ({ back, email }: Props) => {
  const { t } = useTranslation('auth');
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<LoginFormData>({
    defaultValues: {
      username: email || '',
      password: '',
      rememberMe: false,
    },
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const login = async (values: LoginFormData) => {
    try {
      const { data } = await post<LoginResult>('/api/sessions', values);

      // Validate redirects to prevent open redirect vulnerabilities
      // Priority: backend redirectTo > sessionStorage redirect > reload
      const backendRedirect = data.redirectTo
        ? validateRedirect(data.redirectTo)
        : undefined;
      const storageRedirect = getValidatedRedirectFromStorage();

      const redirect = backendRedirect ?? storageRedirect;
      if (redirect) {
        window.location.href = redirect;
      } else {
        window.location.reload();
      }
    } catch {
      // Error handling: login failure is handled by form validation
    }
  };

  return (
    <div className='flex h-screen w-screen items-center justify-center'>
      <div className='w-full lg:w-1/2'>
        <form
          onSubmit={handleSubmit(login)}
          className='mx-auto flex flex-1 flex-col justify-center rounded-lg bg-white px-10 py-16 md:w-4/5 md:border md:border-gray88 lg:mx-auto lg:w-[30rem] lg:flex-initial'
        >
          <div className='mb-12 flex flex-col items-center justify-center'>
            <BrandSquare className='mx-auto h-20' />
            <h1 className='mt-5 text-center text-2xl font-medium'>
              {t('signInText')}
            </h1>
          </div>
          <Input
            {...register('username')}
            label={t('emailAddress')}
            type='email'
            error={errors.username?.message}
          />
          <Input
            {...register('password')}
            label={t('password')}
            type='password'
            error={errors.password?.message}
          />
          <div className='mb-5 flex justify-between text-center text-sm'>
            <div className='flex flex-row-reverse items-center justify-center gap-1 text-left'>
              <label htmlFor='rememberMe'>{t('rememberMe')}</label>
              <input
                id='rememberMe'
                {...register('rememberMe')}
                type='checkbox'
                value='rememberMe'
                className='h-5 w-5 rounded border border-gray88 px-3 py-2.5 '
              />
            </div>

            <Link
              className='text-right font-normal underline'
              to='/forgot-password'
            >
              {t('forgotPassword')}
            </Link>
          </div>
          <button
            className='w-full rounded-lg bg-brand p-3 font-semibold uppercase text-white transition-all duration-300 hover:bg-slate-dark  disabled:opacity-50'
            disabled={isSubmitting}
          >
            {t('login')}
          </button>
          <div className='m-4 inline-flex h-6 items-center justify-center gap-2.5'>
            <div className='h-px w-full bg-black'></div>
            <div className="text-center font-['Figtree'] text-base font-medium leading-snug">
              OR
            </div>
            <div className='h-px w-full bg-black'></div>
          </div>

          <button
            type='button'
            onClick={back}
            className='mb-4 w-full rounded-lg border border-brand bg-white p-3 font-semibold uppercase text-brand'
          >
            log in with one time passcode
          </button>
          <div className='mt-4 text-center text-sm'>
            <Link className='font-normal underline' to='/forgot-password'>
              {t('forgotPassword')}
            </Link>
          </div>
        </form>
      </div>

      <LogoSection />
    </div>
  );
};

// Removed getRedirect() function - now using getValidatedRedirectFromStorage() from helpers/redirect.ts
